import React, { useState, useCallback } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'

/**
 * Hook to manage external {@link ToastNotification} selection.
 *
 * @param {boolean} [defaultOpen=true] - Hide/show the {@link ToastNotification}.
 * @returns {object}
 */
export const useToastOpen = (defaultOpen = true) => {
  const [open, setOpen] = useState(defaultOpen)

  return {
    props: { open: !!open, onClose: useCallback(() => setOpen(false), []) },
    open,
    setOpen,
  }
}

/**
 * Bronson ToastNotifications component.
 * Groups all related {@link ToastNotification}.
 *
 * @param {ToastNotification|ToastNotification[]} [children] - A collection of {@link ToastNotification} components.
 * @param {string} [description] - Description of ToastNotifications.
 * @param {string} [testId] - The `[data-testid]` attribute on the component element.
 * @param {object} [otherProps] - Other props passed to the component element.
 * @returns {JSX.Element} - The ToastNotifications component.
 * @constructor
 */
export function ToastNotifications({ children, description, testId, ...otherProps }) {
  return (
    <aside
      className="c-toast-notifications js-toast-notifications"
      aria-label={description}
      data-testid={testId}
      {...otherProps}
    >
      {children}
    </aside>
  )
}

ToastNotifications.propTypes = {
  children: PropTypes.node, // Bronson template: 'toast-notification'. Use {@link ToastNotification} component.
  description: PropTypes.string, // Bronson template: 'description'.
  testId: PropTypes.string, // Added for `[data-testid]` attribute.
}

/**
 * Bronson ToastNotification component (nested).
 * @see https://bronson.vwfs.tools/default/components/detail/bronson-toast-notification.html
 *
 * @param {string|React.ReactNode} [children] - The ToastNotification content.
 * @param {string} [className] - Additional CSS classes on the component element.
 * @param {string} [closeLabel='Close Toast Notification'] - The label of the close button.
 * @param {boolean} [error] - Indicates the user a failed action, security issues or an error.
 * @param {boolean} [info] - Used to make a comment or a confirmation, give information or make a note.
 * @param {function} [onClose] - Callback when the optional close button (x) is clicked.
 * @param {boolean} [open=true] - Hide/show the ToastNotification.
 * @param {boolean} [success] - Indicates the user a successfully performed action.
 * @param {boolean} [warning] - Indicates the user that an action is potentially dangerous, causes problems or is partly failed.
 * @param {string} [testId] - The `[data-testid]` attribute on the component element.
 * @param {object} [otherProps] - Other props passed to the component element.
 * @returns {JSX.Element|null} - The ToastNotification component.
 * @constructor
 */
export function ToastNotification({
  children,
  className,
  closeLabel = 'Close Toast Notification',
  error,
  info,
  onClose,
  open = true,
  success,
  warning,
  testId,
  ...otherProps
}) {
  const classNameList = classNames(
    'c-toast-notification',
    {
      // Convenience props from Bronson variants.
      'c-toast-notification--default': !(info || success || warning || error),
      'c-toast-notification--info': info,
      'c-toast-notification--success': success,
      'c-toast-notification--warning': warning,
      'c-toast-notification--error': error,
      'is-closed': !open,
    },
    className
  ).trim()

  /**
   * Set the initial height to make CSS animations work properly.
   * @TODO: Should be refactored with the ReactTransitionGroup add-on, @see BRON-7532.
   */
  //   const toastRef = useRef(null)
  //   const [initialHeight, setInitialHeight] = useState(null)

  //   useEffect(() => {
  //     if (toastRef && toastRef.current) {
  //       const h = toastRef.current.clientHeight
  //       if (!!open && !initialHeight) {
  //         setInitialHeight(h)
  //       }
  //     }
  //   }, [open, initialHeight])

  return (
    <article
      className={classNameList}
      aria-live="polite"
      aria-hidden={!open}
      role="status"
      data-testid={testId}
      {...otherProps}
    >
      <div className="c-toast-notification__content">
        <div className="c-toast-notification__body">
          <p>{children}</p>
        </div>
        <button className="c-toast-notification__close" title={closeLabel} type="button" onClick={onClose}>
          <span className="u-visually-hidden">{closeLabel}</span>
        </button>
      </div>
    </article>
  )
}

ToastNotification.propTypes = {
  children: PropTypes.node, // Bronson template: 'text'.
  className: PropTypes.string, // Bronson template: 'modifier'.
  closeLabel: PropTypes.string, // Bronson template: 'close.label'.
  onClose: PropTypes.func, // Added for `[onClick]` attribute.
  open: PropTypes.bool,
  testId: PropTypes.string, // Added for `[data-testid]` attribute.
  /* Convenience props. */
  error: PropTypes.bool, // Convenience prop for `.c-toast-notification--error`. Bronson template: 'modifier'.
  info: PropTypes.bool, // Convenience prop for `.c-toast-notification--info`. Bronson template: 'modifier'.
  success: PropTypes.bool, // Convenience prop for `.c-toast-notification--success`. Bronson template: 'modifier'.
  warning: PropTypes.bool, // Convenience prop for `.c-toast-notification--warning`. Bronson template: 'modifier'.
}
